// The banded board — shared by all three ticker surfaces.
//
// The homepage, the sale-barn ticker (/barn/:slug/ticker) and the state ticker
// (/ticker/:state) all show the same thing: the latest lots, grouped into 100 lb
// weight bands. They used to look nothing alike, because each grew its own
// markup and stylesheet. This is the one board they now share.
//
// ── Presentational ONLY, and that is what makes it shareable ─────────────
// It takes `TickerBand[]` and renders it. It fetches nothing, owns no state and
// knows nothing about scope. That matters because the three surfaces get their
// data in genuinely different ways and cannot be unified at that layer:
//
//   homepage        prerendered + hydrated, so it MUST NOT touch the Supabase
//                   client (that module reads localStorage at import and throws
//                   under Node). It fetches through /api/market.
//   barn / state    ordinary SPA routes with React Query and Realtime
//                   subscriptions, which is right for a logged-in-style page
//                   and wrong for the front door.
//
// Trying to share the DATA layer would mean either dragging React Query into a
// document that mounts no provider, or dragging the prerender's constraints
// onto two pages that do not need them. Sharing the presentation costs nothing
// and gets the whole win.

import { bandMove, barnWithState } from "@/lib/rbCattleTicker";
import type { BandCompare, TickerBand } from "@/lib/rbCattleTicker";

/**
 * The week's move on a band, or nothing at all when there is no honest
 * comparison. Green is up and red is down — the price, not the mood.
 *
 * ⚠️ IT NAMES WHAT IT IS AGAINST, and that is not a nicety. Owner, 2026-09-16,
 * looking at a bare "−$14.96": "is the - red number day over day or what?" —
 * then, on being told: "but we need to show that." A change with no baseline on
 * it is a number the reader has to guess at, and the two readings are worlds
 * apart. Of the 24 barns that sold 500–599 lb steers today, ONE also sold
 * yesterday and NINE sold last Wednesday: day-over-day would set 24 barns
 * against a nearly different 24 and call the difference the market.
 */
/** Head a band needs before the vs-last-week arrow is worth printing.
 *
 *  ⚠️ THE ARROW IS A COIN FLIP ON A THIN BAND. It compares TODAY SO FAR against
 *  last week's FINISHED day, so early in a sale it is a handful of pens against
 *  a full one. Measured on 400–800 lb steers, 2026-08-25 → 09-26, asking how
 *  often the arrow pointed the same way the finished day eventually did:
 *
 *      under 100 hd   57% right, off by $30.99 on average
 *      100–199 hd     76%        $12.35
 *      200–399 hd     83%        $10.10
 *      400–799 hd     90%         $5.60
 *      800+ hd        99%         $2.49
 *
 *  57% is barely better than guessing, on the one number a man reads fastest.
 *  It is HEAD that decides this, not the clock — by hour alone the arrow is
 *  74% right at 11am and 99% by 3pm, but that is an average across bands and
 *  hides the thin ones completely.
 *
 *  400 is the first bucket at 90% and under six dollars of average error. Below
 *  it the price still shows; only the comparison waits for enough cattle to
 *  mean something. */
const MOVE_MIN_HEAD = 400;

function MoveChip({
  vwap,
  compare,
  band,
  head,
  since,
}: {
  vwap: number | null | undefined;
  compare: BandCompare | undefined;
  band: number;
  /** The band's head so far today. Undefined for callers that build bands out
   *  of lot rows and have no aggregate — those get no chip, which is right:
   *  five printed lots are not a sample. */
  head?: number;
  /** "vs last Wed" — formatted in the lib, like every other displayed string. */
  since?: string;
}) {
  if (head == null || head < MOVE_MIN_HEAD) return null;
  const m = bandMove(vwap, compare?.get(band));
  if (!m) return null;
  return (
    // ⚠️ NOT `rbtx-move`. That class is already taken by the rail's index
    // cards (TickerParts), whose colours are tuned for a NAVY card — on this
    // white one they render near-invisible, and the later rule wins.
    <span className="rbtx-bandmove" data-dir={m.dir ?? "flat"}>
      {m.label}
      {/* A real non-breaking space, not a CSS ::before. Generated content is
          not in textContent, so a screen reader and a copied row would both
          get "−$14.96vs last Wed". Same reason as the average row's dot. */}
      {since ? <i>{"\u00a0"}{since}</i> : null}
    </span>
  );
}

export default function BandBoard({
  bands,
  showClass,
  showBarn,
  fresh,
  /** Rendered in the cell after the last band — the homepage has nothing there
   *  now, but the barn and state boards use it for their own eighth cell. */
  tail,
  emptyLabel = "No sales",
  countLabel,
  rich = false,
  compare,
  compareLabel,
  homeCard = false,
}: {
  bands: TickerBand[];
  /**
   * Put the barn's name ON the row.
   *
   * ⚠️ OFF BY DEFAULT, AND THE DEFAULT IS THE HOMEPAGE'S. There the subject is
   * the WEIGHT BAND — "what are 5-weights bringing" — and forty rows each
   * carrying a barn name would bury the prices the page exists to show, which
   * is why the barn lives in the row's `title` there instead.
   *
   * On the Leaderboard the barn IS the answer (it is a board of who got top
   * dollar), and on the Steer Tracker it is the unit. Rendering either of those
   * with the barn hidden in a tooltip would gut the page.
   *
   * ⚠️ SINCE 2026-09-16 EVERY BOARD TURNS IT ON, the three tickers included
   * (owner: "can we have the rows on the tickers be formatted like the leader
   * board rows"), and the earlier small-print variant is gone. A barn's own
   * ticker repeats its name on every row, which the owner chose deliberately
   * over a tidier line: one row shape across the whole site, so a screenshot of
   * any board reads the same. The default stays off only so a caller has to
   * mean it.
   */
  showBarn?: boolean;
  /** Only on a view that MIXES classes. On a single-class board every row
   *  carries the same badge and it is noise on every line. */
  showClass?: boolean;
  /** Lot ids that arrived on the last refresh; they flash once. */
  fresh?: Set<string>;
  tail?: React.ReactNode;
  emptyLabel?: string;
  /** What the band's row count means. Default "N latest" (a ticker's newest
   *  lots); the Steer Tracker's rows are barns, so it says "N barns". */
  countLabel?: (n: number) => string;
  /**
   * Lead each band with its weighted average, the week's move and the day's
   * range, instead of a row count.
   *
   * ⚠️ OFF BY DEFAULT, AND THE DEFAULT IS THE WEBSITE'S. Owner, 2026-09-16:
   * "it looks very basic." The figures were always in the payload and the board
   * showed none of them — a reader had to average five prints in his head to
   * answer the question the page exists for. The app turns this on; the website
   * is unchanged until that is a decision somebody makes on purpose.
   *
   * A flag on this component rather than a second board: the app and the site
   * must never be able to quote different prices for the same cattle.
   */
  rich?: boolean;
  /** Last week's weighted average per band — see fetchBandCompare. Without it
   *  the move is simply absent, never guessed. */
  compare?: BandCompare;
  /** "vs last Wed" — what the move is measured against, said on the row.
   *  Without it the reader cannot tell a week's move from a day's. */
  compareLabel?: string;
  /**
   * The homepage card treatment: a FILLED NAVY BAR for the weight label
   * attached to the top of the card instead of bare text floating above it,
   * and a larger type scale on the rows (owner artwork 2026-09-26, then
   * 2026-09-27: "can we make the green price bigger and the head weight and
   * time of sale bigger").
   *
   * ⚠️ OPT-IN, AND THAT IS THE WHOLE POINT. This component is rendered by the
   * homepage ticker, the barn and state tickers, the barn leaderboard, Foote's
   * customer board, fat cattle, the steer tracker AND the share-card
   * generator. Flipping the look for everyone off one homepage screenshot
   * would restyle a paying customer's page and every share image anybody has
   * ever posted, without anyone asking for it.
   *
   * So the homepage passes it and nothing else does. When another surface
   * wants it, it says so.
   */
  homeCard?: boolean;
}) {
  return (
    <div
      className="rbtx-board"
      data-homecard={homeCard ? "1" : undefined}
    >
      {bands.map((b) => (
        <section key={b.band} aria-label={`${b.label} pounds`}>
          <div className="rbtx-bandhead" data-rich={rich ? "1" : undefined}>
            <div className="rbtx-bandlabel">
              {b.band}–<b>{b.band + 99}</b> lb
            </div>
            {/* ⚠️ NOTHING HERE ON THE APP'S BOARD (owner, 2026-09-16: "way too
                many numbers"). The lot count, the head count, the day's low and
                the day's high were all on this card, and four supporting figures
                around one headline number is how a screen stops being readable.
                What is left is the weight, what it brought, and which way it
                went. The rest is on the barn's own page, where a man goes when
                he wants it. */}
            {!rich && (
              <div className="rbtx-bandcount">
                {b.lots.length
                  ? (countLabel ? countLabel(b.lots.length) : `${b.lots.length} latest`)
                  : "—"}
              </div>
            )}
          </div>


          {b.lots.length === 0 ? (
            // Said plainly rather than left blank. An empty 300–399 on a Monday
            // is normal, and a blank card reads as a broken panel.
            <p className="rbtx-band-empty">{emptyLabel}</p>
          ) : (
            <ol className="rbtx-lots">
              {b.lots.map((lot, i) => (
                <li
                  key={lot.id}
                  className="rbtx-lot"
                  data-rank={i}
                  data-fresh={fresh?.has(lot.id) ? "1" : undefined}
                  // The barn, the time and the per-head figure. The artboard's
                  // row has no room for them, so they live here and in the tape
                  // — a price with no provenance is a rumour.
                  title={`${lot.barn ? `${barnWithState(lot.barn, lot.state)} · ` : ""}${lot.timeLabel} · ${lot.perHeadLabel}/hd`}
                >
                  <span className="rbtx-lot-spec">
                    {showBarn && lot.barn && (
                      <b className="rbtx-lot-barn">{barnWithState(lot.barn, lot.state)}</b>
                    )}
                    {/* The time comes off the app's rows. It was the fourth
                        figure on a line that only needs two — how many, how
                        heavy — and it is the one a man never reads off a board.
                        It stays in the row's tooltip, and on the website, where
                        the row is a tape of arrivals and the clock is the point. */}
                    <span className="rbtx-lot-figs">
                      {lot.headLabel} · {lot.weightLabel}
                      {!rich && showBarn && lot.timeLabel ? ` · ${lot.timeLabel}` : ""}
                    </span>
                  </span>
                  {showClass && lot.classLabel && (
                    <span className="rbtx-lot-class" data-class={lot.cattleType}>
                      {lot.classLabel}
                    </span>
                  )}
                  <span className="rbtx-lot-px">{lot.priceLabel}</span>
                </li>
              ))}
            </ol>
          )}

          {/* ⚠️ THE BAND'S WEIGHTED AVERAGE — SUM(price × head) / SUM(head) over
              the WHOLE band, not an average of the rows above it. It comes from
              rpc_cattle_ticker_bands; the rows are the newest few pens. A single
              pen can sit $40 off the band on a bad set of cattle, and a man
              reading one number in a pickup will take it as the market.

              At the FOOT of the card and labelled (owner, 2026-09-16). In the
              head it sat beside the weight and read as that band's price; under
              the pens it reads as what it is — the line the pens add up to. */}
          {rich && b.vwap != null && (
            <div className="rbtx-bandavg">
              {/* "Wtd Avg" — USDA's own abbreviation on the LMR and auction
                  reports this audience already reads. Abbreviating to something
                  we invented would save the same space and cost the recognition
                  (owner, 2026-09-16: "abbreviate weighted average"). */}
              <span className="rbtx-bandavg-l">
                Wtd Avg
                {/* ⚠️ THE HEAD IS WHAT MAKES THE LABEL HONEST (owner,
                    2026-09-16, having asked what the average covers). "Weighted
                    average" sitting under five pens reads as the average of
                    those five, and it is not — it is every qualifying lot in the
                    band. Today the 500s show five pens from one barn and average
                    84 lots across every barn selling. The head count is the
                    denominator, said out loud. */}
                {/* The dot is real text, not a CSS ::before. Generated content
                    is not in textContent, so a screen reader ran the two
                    together — "weighted average164 hd" — and a copied row did
                    the same. */}
                {b.headLabel ? <i> · {b.headLabel}</i> : null}
              </span>
              <b>{b.vwapLabel}</b>
              <MoveChip vwap={b.vwap} compare={compare} band={b.band} head={b.head} since={compareLabel} />
            </div>
          )}
        </section>
      ))}

      {tail}
    </div>
  );
}
